import type { BatchUpdateAreasDto, BatchUpdateAreasResult } from './types/resource-area';
import { createAppRequest } from "@/services/request";
import { extractDetail } from "@/utils/apiResponse";
import type { CrmScopedViewMode } from "@/modules/zy-crm/utils/crm-view";
import type { ApiRequestOptions } from "./types/common";
import type {
  CompanyEnrichmentQuery,
  CompanyEnrichmentResult,
  AssignCustomerDto,
  BatchAssignCustomersDto,
  BatchClaimCustomersDto,
  BatchDeleteCustomerDto,
  BatchPutCustomersToPoolDto,
  BatchUpdateCustomerStatusDto,
  CreateCustomerAddressDto,
  CreateCustomerTeamMemberDto,
  CreateCustomerDto,
  CustomerAddressItem,
  CustomerDrawerProfile,
  CustomerDrawerSummaryPanel,
  CustomerItem,
  CustomerPageResult,
  CustomerQueryParams,
  PutCustomerToPoolDto,
  UpdateCustomerAddressDto,
  UpdateCustomerTeamMemberRoleDto,
  UpdateCustomerDto,
} from "./types/customer";
import { extractList, extractTotal } from "@/utils/apiResponse";

const request = createAppRequest();
const BASE_PATH = "/bff/zy-crm/customers";

type DetailScopeOptions = ApiRequestOptions & {
  params?: Record<string, unknown>;
  viewMode?: CrmScopedViewMode;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

const normalizeBooleanField = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (Array.isArray(value)) {
    return normalizeBooleanField(value[0]);
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
  }
  if (isObject(value)) {
    const bufferLike = (value as Record<string, unknown>).data;
    if (Array.isArray(bufferLike)) {
      return normalizeBooleanField(bufferLike[0]);
    }
  }
  return Boolean(value);
};

const normalizeCustomerItem = (value: unknown): CustomerItem => {
  const item = (value || {}) as CustomerItem & Record<string, unknown>;
  return {
    ...item,
    lockStatus: normalizeBooleanField(item.lockStatus),
    dealStatus: normalizeBooleanField(item.dealStatus),
    isDeleted:
      item.isDeleted === undefined
        ? undefined
        : normalizeBooleanField(item.isDeleted),
  };
};

const normalizeCustomerPage = (resp: unknown): CustomerPageResult => {
  if (!isObject(resp)) {
    throw new Error("客户列表返回结构异常，请检查 API 地址或代理配置");
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data.map((item: unknown) => normalizeCustomerItem(item)),
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }

  throw new Error("客户列表返回结构异常，请检查 API 地址或代理配置");
};

const unwrapCustomerDetail = (resp: unknown): CustomerItem => {
  if (resp && typeof resp === "object") {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== "object") {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === "string") {
        return normalizeCustomerItem(candidate);
      }
    }
  }
  throw new Error("客户详情返回结构异常");
};

export async function queryCustomers(
  params: CustomerQueryParams,
  options?: ApiRequestOptions
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: "GET",
    params: {
      ...params,
      tagUuids: Array.isArray(params.tagUuids)
        ? params.tagUuids.join(",")
        : params.tagUuids,
    },
    ...(options || {}),
  });
  return normalizeCustomerPage(resp);
}

export async function queryCustomerDetail(
  uuid: string,
  options?: DetailScopeOptions
) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: "GET",
    params: {
      ...(params || {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
  return unwrapCustomerDetail(resp);
}

export async function queryCustomerDrawerProfile(
  uuid: string,
  options?: DetailScopeOptions
) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<CustomerDrawerProfile | { data?: CustomerDrawerProfile }>(
    `${BASE_PATH}/${uuid}/drawer-profile`,
    {
      method: "GET",
      params: {
        ...(params || {}),
        ...(viewMode ? { viewMode } : {}),
      },
      ...rest,
    }
  );
  return extractDetail(resp) as CustomerDrawerProfile;
}

export async function queryCustomerAddresses(
  uuid: string,
  options?: ApiRequestOptions
) {
  const resp = await request(`${BASE_PATH}/${uuid}/addresses`, {
    method: "GET",
    ...(options || {}),
  });
  return {
    list: extractList(resp as { list?: CustomerAddressItem[] }),
    total: extractTotal(resp as { total?: number; list?: CustomerAddressItem[] }),
  };
}

export async function createCustomerAddress(
  uuid: string,
  payload: CreateCustomerAddressDto,
  options?: ApiRequestOptions
) {
  return request<CustomerAddressItem>(`${BASE_PATH}/${uuid}/addresses`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function updateCustomerAddress(
  uuid: string,
  addressUuid: string,
  payload: UpdateCustomerAddressDto,
  options?: ApiRequestOptions
) {
  return request<CustomerAddressItem>(
    `${BASE_PATH}/${uuid}/addresses/${addressUuid}`,
    {
      method: "PATCH",
      data: payload,
      ...(options || {}),
    }
  );
}

export async function deleteCustomerAddress(
  uuid: string,
  addressUuid: string,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/${uuid}/addresses/${addressUuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export async function createCustomerTeamMember(
  uuid: string,
  payload: CreateCustomerTeamMemberDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateCustomerTeamMemberRole(
  uuid: string,
  teamMemberUuid: string,
  payload: UpdateCustomerTeamMemberRoleDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteCustomerTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function createCustomer(
  payload: CreateCustomerDto,
  options?: ApiRequestOptions
) {
  return request<CustomerItem>(BASE_PATH, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function updateCustomer(
  uuid: string,
  payload: UpdateCustomerDto,
  options?: ApiRequestOptions
) {
  return request<CustomerItem>(`${BASE_PATH}/${uuid}`, {
    method: "PATCH",
    data: payload,
    ...(options || {}),
  });
}

export async function deleteCustomer(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export async function batchDeleteCustomers(
  payload: BatchDeleteCustomerDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-delete`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateCustomerStatus(
  payload: BatchUpdateCustomerStatusDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-status`, {
    method: "PATCH",
    data: payload,
    ...(options || {}),
  });
}

export async function restoreCustomer(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/${uuid}/restore`, {
    method: "PATCH",
    ...(options || {}),
  });
}

export async function putCustomerToPool(
  uuid: string,
  payload: PutCustomerToPoolDto,
  options?: ApiRequestOptions
) {
  return request<CustomerItem>(`${BASE_PATH}/${uuid}/pool`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchPutCustomersToPool(
  payload: BatchPutCustomersToPoolDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-pool`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function claimCustomer(uuid: string, options?: ApiRequestOptions) {
  return request<CustomerItem>(`${BASE_PATH}/${uuid}/claim`, {
    method: "POST",
    ...(options || {}),
  });
}

export async function assignCustomer(
  uuid: string,
  payload: AssignCustomerDto,
  options?: ApiRequestOptions
) {
  return request<CustomerItem>(`${BASE_PATH}/${uuid}/assign`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchClaimCustomers(
  payload: BatchClaimCustomersDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-claim`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchAssignCustomers(
  payload: BatchAssignCustomersDto,
  options?: ApiRequestOptions
) {
  return request<{ uuids: string[]; memberUuid: string }>(
    `${BASE_PATH}/batch-assign`,
    {
      method: "POST",
      data: payload,
      ...(options || {}),
    }
  );
}

export interface CustomerOverviewStatistics {
  total: number;
  dueToday: number;
  stale: number;
  deal: number;
  pool: number;
}

export async function queryCustomerOverview(options?: ApiRequestOptions): Promise<CustomerOverviewStatistics> {
  const response = await request<CustomerOverviewStatistics>(`${BASE_PATH}/overview`, { method: 'GET', ...options });
  const data = extractDetail(response);
  if (!data || !Number.isFinite(Number(data.total))) throw new Error('概览统计返回结构异常');
  return data;
}

export async function batchUpdateCustomerAreas(payload: BatchUpdateAreasDto, options?: ApiRequestOptions) {
  return request<BatchUpdateAreasResult>(`${BASE_PATH}/batch-areas`, {
    method: 'PATCH', data: payload, ...(options || {}),
  });
}

export async function enrichCustomerCompany(payload: CompanyEnrichmentQuery, signal?: AbortSignal) {
  return request<CompanyEnrichmentResult>(`${BASE_PATH}/company-enrichment`, {
    method: 'POST', data: payload, signal, timeout: 100_000, skipErrorHandler: true,
  });
}
